import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Car, Truck, Bike, Bus, Camera, Search, Plus, X, Check, 
  LogIn, LogOut, Image, Settings, Video, RefreshCw, AlertCircle,
  ChevronDown, Filter, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // Form state for new vehicle registration
  const [newVehicle, setNewVehicle] = useState({
    license_plate: '',
    vehicle_type: 'car',
    make: '',
    model: '',
    color: '',
    notes: ''
  });

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch vehicles
  const { data: vehicles = [], isLoading } = useQuery({
    queryKey: ['vehicles', eventId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (eventId) params.append('event_id', eventId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await fetch(`${API_URL}/api/vehicles?${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch vehicles');
      return res.json();
    }
  });

  // Fetch vehicle stats
  const { data: stats = {} } = useQuery({
    queryKey: ['vehicle-stats', eventId],
    queryFn: async () => {
      const params = eventId ? `?event_id=${eventId}` : '';
      const res = await fetch(`${API_URL}/api/vehicles/stats/summary${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    }
  });

  // Fetch cameras
  const { data: cameras = [] } = useQuery({
    queryKey: ['cameras'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/cameras`, { headers });
      if (!res.ok) throw new Error('Failed to fetch cameras');
      return res.json();
    }
  });

  // Register vehicle mutation
  const registerMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/vehicles`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...data, event_id: eventId })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to register vehicle');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['vehicles']);
      queryClient.invalidateQueries(['vehicle-stats']);
      setShowAddDialog(false);
      setNewVehicle({ license_plate: '', vehicle_type: 'car', make: '', model: '', color: '', notes: '' });
      toast.success('Vehicle registered successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/vehicles/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Check-in failed');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['vehicles']);
      queryClient.invalidateQueries(['vehicle-stats']);
      setShowCheckInDialog(false);
      setCapturedPhoto(null);
      toast.success(data.message);
    },
    onError: (error) => toast.error(error.message)
  });

  // Check-out mutation
  const checkOutMutation = useMutation({
    mutationFn: async (vehicleId) => {
      const res = await fetch(`${API_URL}/api/vehicles/${vehicleId}/check-out`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Check-out failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['vehicles']);
      queryClient.invalidateQueries(['vehicle-stats']);
      toast.success('Vehicle checked out');
    },
    onError: (error) => toast.error(error.message)
  });

  // Camera functions
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: 1280, height: 720 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      toast.error('Could not access camera');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(dataUrl);
    }
  }, []);

  // Filter vehicles
  const filteredVehicles = vehicles.filter(v => 
    v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const vehicleTypeIcon = (type) => {
    switch (type) {
      case 'motorcycle': return <Bike className="h-4 w-4" />;
      case 'truck': return <Truck className="h-4 w-4" />;
      case 'bus': return <Bus className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };

  const statusBadge = (status) => {
    const colors = {
      'registered': 'bg-blue-100 text-blue-800',
      'checked_in': 'bg-green-100 text-green-800',
      'checked_out': 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="vehicle-management">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Vehicles</p>
                <p className="text-2xl font-bold">{stats.total_vehicles || 0}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Currently On-Site</p>
                <p className="text-2xl font-bold text-green-600">{stats.currently_checked_in || 0}</p>
              </div>
              <LogIn className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Check-ins</p>
                <p className="text-2xl font-bold text-purple-600">{stats.today_check_ins || 0}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cameras Active</p>
                <p className="text-2xl font-bold text-orange-600">{cameras.filter(c => c.is_active).length}</p>
              </div>
              <Camera className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search plate, make, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="vehicle-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCameraDialog(true)} data-testid="view-cameras-btn">
            <Video className="h-4 w-4 mr-2" />
            Live Cameras
          </Button>
          <Button variant="outline" onClick={() => setShowCheckInDialog(true)} data-testid="quick-checkin-btn">
            <LogIn className="h-4 w-4 mr-2" />
            Quick Check-in
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="register-vehicle-btn">
            <Plus className="h-4 w-4 mr-2" />
            Register Vehicle
          </Button>
        </div>
      </div>

      {/* Vehicles Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading vehicles...</div>
          ) : filteredVehicles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No vehicles found. Register a vehicle to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">License Plate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Make/Model</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredVehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50" data-testid={`vehicle-row-${vehicle.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {vehicleTypeIcon(vehicle.vehicle_type)}
                          <span className="capitalize">{vehicle.vehicle_type || 'Car'}</span>
                          {vehicle.color && (
                            <div 
                              className="w-4 h-4 rounded-full border" 
                              style={{ backgroundColor: vehicle.color.toLowerCase() }}
                              title={vehicle.color}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-lg">{vehicle.license_plate}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : '-'}
                      </td>
                      <td className="px-4 py-3">{statusBadge(vehicle.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {vehicle.last_check_in ? new Date(vehicle.last_check_in).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {vehicle.status === 'checked_in' ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => checkOutMutation.mutate(vehicle.id)}
                              data-testid={`checkout-btn-${vehicle.id}`}
                            >
                              <LogOut className="h-4 w-4 mr-1" />
                              Check Out
                            </Button>
                          ) : (
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedVehicle(vehicle);
                                setShowCheckInDialog(true);
                              }}
                              data-testid={`checkin-btn-${vehicle.id}`}
                            >
                              <LogIn className="h-4 w-4 mr-1" />
                              Check In
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Register Vehicle Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) setNewVehicle({ license_plate: '', vehicle_type: 'car', make: '', model: '', color: '', notes: '' });
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register New Vehicle</DialogTitle>
          </DialogHeader>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              if (!newVehicle.license_plate.trim()) {
                toast.error('License plate is required');
                return;
              }
              registerMutation.mutate(newVehicle);
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-sm font-medium">License Plate *</label>
              <Input 
                value={newVehicle.license_plate}
                onChange={(e) => setNewVehicle(prev => ({ ...prev, license_plate: e.target.value }))}
                required 
                placeholder="ABC 123" 
                className="font-mono" 
                data-testid="input-license-plate" 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Vehicle Type</label>
                <Select 
                  value={newVehicle.vehicle_type} 
                  onValueChange={(value) => setNewVehicle(prev => ({ ...prev, vehicle_type: value }))}
                >
                  <SelectTrigger data-testid="select-vehicle-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="bus">Bus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Color</label>
                <Input 
                  value={newVehicle.color}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="Black, White..." 
                  data-testid="input-color" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Make</label>
                <Input 
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, make: e.target.value }))}
                  placeholder="Toyota, Honda..." 
                  data-testid="input-make" 
                />
              </div>
              <div>
                <label className="text-sm font-medium">Model</label>
                <Input 
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Camry, Civic..." 
                  data-testid="input-model" 
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input 
                value={newVehicle.notes}
                onChange={(e) => setNewVehicle(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..." 
                data-testid="input-notes" 
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={registerMutation.isPending} data-testid="submit-register-btn">
                {registerMutation.isPending ? 'Registering...' : 'Register Vehicle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Check-in Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={(open) => {
        setShowCheckInDialog(open);
        if (!open) {
          stopCamera();
          setCapturedPhoto(null);
          setSelectedVehicle(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vehicle Check-in</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="manual">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              <TabsTrigger value="camera" onClick={startCamera}>Camera Capture</TabsTrigger>
            </TabsList>
            <TabsContent value="manual" className="space-y-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = new FormData(e.target);
                  checkInMutation.mutate({
                    license_plate: selectedVehicle?.license_plate || form.get('license_plate'),
                    gate: form.get('gate'),
                    photo_base64: capturedPhoto
                  });
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">License Plate</label>
                    <Input 
                      name="license_plate" 
                      defaultValue={selectedVehicle?.license_plate || ''}
                      required 
                      placeholder="Enter plate number"
                      className="font-mono text-lg"
                      data-testid="checkin-license-plate"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Gate</label>
                    <Select name="gate" defaultValue="main">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="main">Main Gate</SelectItem>
                        <SelectItem value="north">North Gate</SelectItem>
                        <SelectItem value="south">South Gate</SelectItem>
                        <SelectItem value="vip">VIP Gate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {capturedPhoto && (
                    <div className="relative">
                      <img src={capturedPhoto} alt="Captured" className="w-full rounded-lg" />
                      <Button 
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={() => setCapturedPhoto(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCheckInDialog(false)}>Cancel</Button>
                    <Button type="submit" disabled={checkInMutation.isPending} data-testid="confirm-checkin-btn">
                      <Check className="h-4 w-4 mr-2" />
                      {checkInMutation.isPending ? 'Processing...' : 'Check In'}
                    </Button>
                  </div>
                </div>
              </form>
            </TabsContent>
            <TabsContent value="camera" className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video ref={videoRef} autoPlay playsInline className="w-full" />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="flex justify-center gap-2">
                <Button onClick={capturePhoto} data-testid="capture-photo-btn">
                  <Camera className="h-4 w-4 mr-2" />
                  Capture Photo
                </Button>
              </div>
              {capturedPhoto && (
                <div className="space-y-2">
                  <img src={capturedPhoto} alt="Captured" className="w-full rounded-lg" />
                  <p className="text-sm text-gray-500 text-center">
                    Photo captured. Enter license plate manually above to check in.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Live Cameras Dialog */}
      <Dialog open={showCameraDialog} onOpenChange={setShowCameraDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Live Camera Feeds</DialogTitle>
          </DialogHeader>
          {cameras.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Camera className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No cameras configured</p>
              <p className="text-sm">Add network cameras in System Settings</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cameras.map((camera) => (
                <Card key={camera.id}>
                  <CardHeader className="py-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{camera.name}</CardTitle>
                      <Badge variant={camera.is_active ? "default" : "secondary"}>
                        {camera.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">{camera.location}</p>
                  </CardHeader>
                  <CardContent className="p-2">
                    {camera.is_active && camera.stream_type === 'mjpeg' ? (
                      <img 
                        src={camera.stream_url}
                        alt={camera.name}
                        className="w-full h-48 object-cover rounded bg-gray-900"
                        onError={(e) => e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">📷</text></svg>'}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-900 rounded flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          <Video className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">{camera.stream_type?.toUpperCase()} Stream</p>
                          <p className="text-xs">Connect via VLC or similar</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleManagement;
